"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  FormControl,
  FormLabel,
  Text,
  HStack,
  NumberInput,
  NumberInputField,
  Box,
} from "@chakra-ui/react";

interface ReminderMessageSettingsProps {
  triggerMs: number | undefined;
  maxCount: number | undefined;
  onChange: (triggerMs: number, maxCount: number) => Promise<boolean> | void;
}

export const ReminderMessageSettings: React.FC<
  ReminderMessageSettingsProps
> = ({ triggerMs = 10000, maxCount = 1, onChange }) => {
  const [seconds, setSeconds] = useState(Math.floor(triggerMs / 1000));
  const [count, setCount] = useState(maxCount);
  const [isUpdating, setIsUpdating] = useState(false);

  // Sync local state when props change
  useEffect(() => {
    setSeconds(Math.floor(triggerMs / 1000));
    setCount(maxCount);
  }, [triggerMs, maxCount]);

  const handleSecondsChange = (valueAsString: string) => {
    const value = Number.parseInt(valueAsString, 10);
    if (!Number.isNaN(value)) setSeconds(value);
  };

  const handleCountChange = (valueAsString: string) => {
    const value = Number.parseInt(valueAsString, 10);
    if (!Number.isNaN(value)) setCount(value);
  };

  const handleBlur = async () => {
    const newTriggerMs = seconds * 1000;
    if (newTriggerMs !== triggerMs || count !== maxCount) {
      setIsUpdating(true);
      try {
        await onChange(newTriggerMs, count);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <FormControl>
      <FormLabel fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>
        Reminder Message Frequency
      </FormLabel>
      <Text fontSize="xs" color="gray.600" mb={2}>
        Control how often AI will send a reminder message.
      </Text>

      <HStack spacing={4}>
        <Box>
          <NumberInput
            min={1}
            max={60}
            value={seconds}
            onChange={handleSecondsChange}
            onBlur={handleBlur}
            isDisabled={isUpdating}
            size="sm"
            w="80px"
            clampValueOnBlur
          >
            <NumberInputField
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "gray.400" }}
              _focus={{
                borderColor: "primary.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
              }}
            />
          </NumberInput>
          <Text fontSize="xs" color="gray.600" mt={1} textAlign="center">
            seconds
          </Text>
        </Box>

        <Box>
          <NumberInput
            min={0}
            max={10}
            value={count}
            onChange={handleCountChange}
            onBlur={handleBlur}
            isDisabled={isUpdating}
            size="sm"
            w="80px"
            clampValueOnBlur
          >
            <NumberInputField
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "gray.400" }}
              _focus={{
                borderColor: "primary.500",
                boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
              }}
            />
          </NumberInput>
          <Text fontSize="xs" color="gray.600" mt={1} textAlign="center">
            times
          </Text>
        </Box>
      </HStack>
    </FormControl>
  );
};
